import type { SubmissionWithPicks } from '@/lib/queries'
import { describeSubmissionPicks, type SubmissionPick } from '@/lib/submissionPicks'
import type { Attendee } from '@/types/database'

interface Props {
  submission: SubmissionWithPicks
  allSubmissions: readonly SubmissionWithPicks[]
  attendees: readonly Attendee[]
  /**
   * False when this person sent something after this entry, so what is on
   * screen is not what the matchmaker will read.
   */
  isNewestForSubmitter: boolean
  onClose: () => void
}

/** How a pick reads at a glance, without making the organiser decode flags. */
function pickStatus(pick: SubmissionPick): { label: string; className: string } {
  if (pick.missing) return { label: 'removed', className: 'badge badge-danger' }
  if (pick.mutual) return { label: 'picked back', className: 'badge badge-success' }
  if (pick.awaitingSubmission) return { label: 'not submitted yet', className: 'badge' }
  return { label: 'did not pick back', className: 'badge badge-warning' }
}

export function SubmissionDetailCard({
  submission,
  allSubmissions,
  attendees,
  isNewestForSubmitter,
  onClose,
}: Props) {
  const { picks, mutualCount } = describeSubmissionPicks(submission, allSubmissions, attendees)

  const submitterName = `${submission.first_name} ${submission.last_name ?? ''}`.trim()

  return (
    <div className="card">
      <div className="card-header">
        <h2>
          <span className="cell-number">{submission.submitted_matchmaking_id}</span> {submitterName}
        </h2>
        <button className="btn btn-sm btn-ghost" type="button" onClick={onClose}>
          Close
        </button>
      </div>

      <div className="metric-grid">
        <div className="metric">
          <span className="metric-label">Picked</span>
          <span className="metric-value">{picks.length}</span>
        </div>
        <div className="metric">
          <span className="metric-label">Picked back</span>
          <span className="metric-value">{mutualCount}</span>
        </div>
      </div>

      <div className="stack">
        <p className="field-hint">
          Submitted {new Date(submission.submitted_at).toLocaleString()} · {submission.email}
          {submission.phone !== null && ` · ${submission.phone}`}
        </p>
      </div>

      {/* Everything below is worked out from this entry, so say up front when
          this entry is not the one that counts. */}
      {!isNewestForSubmitter && (
        <p className="banner banner-warning">
          {submitterName} sent a later answer, and that is the one the matchmaker reads. These picks
          count for nothing unless you delete the newer entry.
        </p>
      )}

      {submission.comment !== null && (
        <div className="stack">
          <h3>Their note</h3>
          <p className="field-hint">
            Written by {submitterName} on the form. Often a number that was missing from the pick
            list, which you may need to add by hand on the Attendees tab.
          </p>
          <blockquote className="quote">{submission.comment}</blockquote>
        </div>
      )}

      {picks.length === 0 ? (
        <p className="field-hint">
          They submitted the form without choosing anyone. That is a valid answer, and they will get
          the no-matches email.
        </p>
      ) : (
        <div className="table-responsive">
          <div className="table-wrap">
            <table>
              <caption className="sr-only">
                People {submitterName} picked, in the order they chose them
              </caption>
              <thead>
                <tr>
                  <th scope="col">No.</th>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Phone</th>
                  <th scope="col">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {picks.map((pick) => {
                  const status = pickStatus(pick)

                  return (
                    <tr key={pick.attendeeId} className={pick.missing ? 'row-inactive' : undefined}>
                      <td data-label="No." className="cell-number">
                        {pick.matchmakingId || '—'}
                      </td>
                      <td data-label="Name">{pick.name}</td>
                      <td data-label="Email" className="cell-muted">
                        {pick.email ?? '—'}
                      </td>
                      <td data-label="Phone" className="cell-muted">
                        {pick.phone ?? '—'}
                      </td>
                      <td data-label="Outcome">
                        <span className={status.className}>{status.label}</span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="field-hint">
        &ldquo;Picked back&rdquo; is worked out from the submissions as they stand right now, using
        the same mutual rule as the matchmaker: each person&rsquo;s newest entry, and only that one.
        If people have submitted since the last run, this can differ from the saved results — re-run
        matchmaking to bring them into line.
      </p>
    </div>
  )
}
